import { useContext, useMemo } from "react";

import { IntlContext } from "context";
import { getCategoryVisual, isActiveServiceCategory } from "constants/categories";
import type { CatalogService } from "types/service";
import { SUBCATEGORY_TO_CATEGORY, type SubCategory } from "types/subCategory";

export type ProviderServiceChipLevel = "category" | "subcategory";

interface ProviderServiceChip {
    key: string;
    label: string;
    palette: ReturnType<typeof getCategoryVisual>["palette"];
    Icon: ReturnType<typeof getCategoryVisual>["Icon"];
}

function resolveCategoryKey(service: CatalogService): string {
    const categoryFromSubcategory =
        service.subcategory &&
        SUBCATEGORY_TO_CATEGORY[service.subcategory as SubCategory]?.toLowerCase();

    return categoryFromSubcategory ?? String(service.category ?? "").toLowerCase();
}

export function useProviderServiceChips(
    catalog: CatalogService[],
    level: ProviderServiceChipLevel = "subcategory",
): ProviderServiceChip[] {
    const intl = useContext(IntlContext);

    return useMemo(() => {
        const seen = new Set<string>();

        return catalog.flatMap((service) => {
            const categoryKey = resolveCategoryKey(service);
            if (!isActiveServiceCategory(categoryKey)) {
                return [];
            }

            const dedupeKey = level === "category" ? categoryKey : service.subcategory ? String(service.subcategory) : categoryKey;
            if (seen.has(dedupeKey)) {
                return [];
            }
            seen.add(dedupeKey);

            const { palette, Icon } = getCategoryVisual(categoryKey);
            const label =
                level === "category"
                    ? intl.formatMessage(`category.${categoryKey}`)
                    : service.subcategory
                      ? intl.formatMessage(`our_services.label.${service.subcategory}`)
                      : intl.formatMessage(`category.${categoryKey}`);

            return [{ key: dedupeKey, label, palette, Icon }];
        });
    }, [catalog, intl, level]);
}
